import React from 'react';
import MainContainer from '../Components/Elements/MainContainer';
import Title from '../Components/Elements/Title';
import { Formik } from 'formik';
import { RSVPFormValues } from '../Models/RSVPFormValues';
import AdaptiveContainer from '../Components/Elements/AdaptiveContainer';
import menuJson from '../Data/menu.json';
import childrenMenuJson from '../Data/childrenMenu.json';
import { Dish } from '../Models/Dish';
import { Helmet } from 'react-helmet';
import { useStore } from '../Stores/store';
import { UserType } from '../Models/UserType';
import Text from '../Components/Elements/Text';
import { observer } from 'mobx-react-lite';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';

const RSVP = observer(() => {
    const { generalStore } = useStore();
    const { userType, rsvpFormLoading, postRSVP, rsvpFormVals } = generalStore;
    const navigate = useNavigate();

    const formVals : RSVPFormValues = rsvpFormVals ?? {
        firstName: '',
        lastName: '',
        allergiesOrDietaryRequirements: '',
        starter: '',
        main: '',
        dessert: '',
        attending: undefined,
        userType: userType,
        isChildMeal: "false",
        songRequest: '',
        otherRequests: ''
    }

    const validationSchema = Yup.object().shape({
        firstName: Yup.string().required('First Name is required').max(50, 'First Name is too long'),
        lastName: Yup.string().required('Last Name is required').max(50, 'First Name is too long'),
        attending: Yup.string().required('Attending is required').oneOf(['true', 'false'], 'Attending must be either true or false'),
        isChildMeal: Yup.string().when(['attending', 'userType'], {
            is: (attending: string, userType: UserType) => attending === 'true' && userType === UserType.DayGuest,
            then: (schema) => schema.required('Child meal is required').oneOf(['true', 'false'], 'Child meal must be either true or false')
        }),
        starter: Yup.string().when(['isChildMeal', 'attending', 'userType'], {
            is: (isChildMeal: string, attending: string, userType: UserType) =>
                isChildMeal === "false" && attending === "true" && userType === UserType.DayGuest,
            then: (schema) => schema.required('Starter is required')
        }),
        main: Yup.string().when(['isChildMeal', 'attending', 'userType'], {
            is: (isChildMeal: string, attending: string, userType: UserType) =>
                isChildMeal === "false" && attending === "true" && userType === UserType.DayGuest,
            then: (schema) => schema.required('Main is required')
        }),
        dessert: Yup.string().when(['isChildMeal', 'attending', 'userType'], {
            is: (isChildMeal: string, attending: string, userType: UserType) =>
                isChildMeal === "false" && attending === "true" && userType === UserType.DayGuest,
            then: (schema) => schema.required('Dessert is required')
        })
    });

    const handleSubmit = async (values : RSVPFormValues) => {
        const success = await postRSVP(values);
        if (success) {
            navigate('/app/rsvp/thanks')
        }
    }

    const getDietary = (dish : Dish) => {
        if (dish.vegan) {
            return <span className='text-green-600'>(Vegan)</span>
        }

        if (dish.vegetarian) {
            return <span className='text-green-600'>(Vegetarian)</span>
        }

        return <></>
    }

    const renderRadio = (dish : Dish) => {
        return (
            <div>
                <div className="flex flex-col max-w-sm">
                    <Text className="font-bold" alignment='left'>{dish.name} {getDietary(dish)}</Text>
                    <Text className="text-gray-700" alignment='left'>{dish.description}</Text>
                    { dish.allergens && <Text className="text-gray-700" alignment='left'>Allergens: {dish.allergens.join(", ")}</Text> }
                </div>
            </div>)
    }

    return (
        <MainContainer>
            <Helmet>
                <meta charSet="utf-8" />
                <title>H&R Wedding | RSVP</title>
            </Helmet>
            <Title size="3" className='mb-4'>RSVP</Title>
            <Text className="mb-4">Please fill out the following form to inform us of your attendance.</Text>
            <Formik
                initialValues={formVals}
                onSubmit={(values, { setSubmitting }) => {
                    handleSubmit(values);
                    setSubmitting(false);
                }}
                enableReinitialize
                validationSchema={validationSchema}
                validateOnChange={false}
                validateOnBlur={false}
            >
                {({handleSubmit, handleChange, values, errors, touched, isValid}) => (
                <form onSubmit={handleSubmit} className="flex flex-col items-center">

                    <AdaptiveContainer columns={1}>
                        { errors.firstName && touched.firstName && <div className="text-red-500">{errors.firstName}</div>}
                        <label htmlFor="firstName" className="font-bold font-lora">First Name<span className="text-red-600">*</span></label>
                        <input type="text" name="firstName" id="firstName" value={values.firstName} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200" />
                    </AdaptiveContainer>
                    <AdaptiveContainer columns={1}>
                        { errors.lastName && touched.lastName && <div className="text-red-500">{errors.lastName}</div>}
                        <label htmlFor="lastName" className="font-bold font-lora">Last Name<span className="text-red-600">*</span></label>
                        <input type="text" name="lastName" id="lastName" value={values.lastName} onChange={handleChange}  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200" />
                    </AdaptiveContainer>
                    <AdaptiveContainer columns={1}>
                        { errors.attending && touched.attending && <div className="text-red-500">{errors.attending}</div>}
                        <label htmlFor="attending" className="font-bold font-lora">Attending?<span className="text-red-600">*</span></label>
                        <div className="flex gap-2">
                            <label className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer transition duration-200">
                                <input
                                type="radio"
                                name="attending"
                                value={"true"}
                                checked={values.attending === "true"}
                                onChange={handleChange}
                                className="h-5 w-5 text-blue-500 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-gray-700">Yes</span>
                            </label>
                            <label className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer transition duration-200">
                                <input
                                type="radio"
                                name="attending"
                                value={"false"}
                                checked={values.attending === "false"}
                                onChange={handleChange}
                                className="h-5 w-5 text-blue-500 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-gray-700">No</span>
                            </label>
                        </div>
                        </AdaptiveContainer>
  
                    {
                        values.attending === "true" && userType === UserType.DayGuest && (

                        <AdaptiveContainer columns={1}>
                            <AdaptiveContainer columns={1}>
                                { errors.isChildMeal && touched.isChildMeal && <div className="text-red-500">{errors.isChildMeal}</div>}
                                <label htmlFor="isChildMeal" className="font-bold font-lora">Is this a Child&apos;s meal?<span className="text-red-600">*</span></label>
                                <div className="flex gap-2">
                                    <label className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer transition duration-200">
                                        <input
                                        type="radio"
                                        name="isChildMeal"
                                        value={"true"}
                                        checked={values.isChildMeal === "true"}
                                        onChange={(e) => {
                                            handleChange(e);
                                            if (e.target.value === "true") {
                                                values.main = '';
                                                values.starter = '';
                                                values.dessert = '';
                                            }
                                        }}
                                        className="h-5 w-5 text-blue-500 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-gray-700">Yes</span>
                                    </label>
                                    <label className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer transition duration-200">
                                        <input
                                        type="radio"
                                        name="isChildMeal"
                                        value={"false"}
                                        checked={values.isChildMeal === "false"}
                                        onChange={handleChange}
                                        className="h-5 w-5 text-blue-500 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-gray-700">No</span>
                                    </label>
                                </div>
                            </AdaptiveContainer>
                            <AdaptiveContainer columns={1}>
                                <label htmlFor="allergiesOrDietaryRequirements" className="font-bold font-lora">Allergies or dietary requirements</label>
                                <input type="text" name="allergiesOrDietaryRequirements" id="allergiesOrDietaryRequirements" value={values.allergiesOrDietaryRequirements} onChange={handleChange}  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200" />
                            </AdaptiveContainer>

                        { values.isChildMeal === "false" && (
                            <div>
                            <Title size="2">Starters<span className="text-red-600">*</span></Title>
                            <AdaptiveContainer columns={1}>
                            { errors.starter && touched.starter && <div className="text-red-500">{errors.starter}</div>}
                            { menuJson.starters.map((starter : Dish) => (
                                <label
                                    key={starter.name}
                                    className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer transition duration-200"
                                >
                                    <input
                                    type="radio"
                                    name="starter"
                                    value={starter.name}
                                    checked={values.starter === starter.name}
                                    onChange={handleChange}
                                    className="h-5 w-5 text-blue-500 border-gray-300 focus:ring-blue-500"
                                    />
                                    {renderRadio(starter)}
                                </label>
                            ))}
                            </AdaptiveContainer>

                            <Title size="2">Mains<span className="text-red-600">*</span></Title>
                            <AdaptiveContainer columns={1}>
                            { errors.main && touched.main && <div className="text-red-500">{errors.main}</div>}
                            { menuJson.mains.map((main : Dish) => (
                                <label
                                    key={main.name}
                                    className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer transition duration-200"
                                >
                                    <input
                                    type="radio"
                                    name="main"
                                    value={main.name}
                                    checked={values.main === main.name}
                                    onChange={handleChange}
                                    className="h-5 w-5 text-blue-500 border-gray-300 focus:ring-blue-500"
                                    />
                                    {renderRadio(main)}
                                </label>
                            ))}
                            </AdaptiveContainer>

                            <Title size="2">Desserts<span className="text-red-600">*</span></Title>
                            <AdaptiveContainer columns={1}>
                            { errors.dessert && touched.dessert && <div className="text-red-500">{errors.dessert}</div>}
                            { menuJson.desserts.map((dessert : Dish) => (
                                <label
                                    key={dessert.name}
                                    className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer transition duration-200"
                                >
                                    <input
                                    type="radio"
                                    name="dessert"
                                    value={dessert.name}
                                    checked={values.dessert === dessert.name}
                                    onChange={handleChange}
                                    className="h-5 w-5 text-blue-500 border-gray-300 focus:ring-blue-500"
                                    />
                                    {renderRadio(dessert)}
                                </label>
                            ))}
                            </AdaptiveContainer>
                            </div>
                        )}
                        { values.isChildMeal === "true" && (
                            <div>
                                <Title size="2">Starters<span className="text-red-600">*</span></Title>
                            <AdaptiveContainer columns={1}>
                            { errors.starter && touched.starter && <div className="text-red-500">{errors.starter}</div>}
                            { childrenMenuJson.starters.map((starter : Dish) => (
                                <label
                                    key={starter.name}
                                    className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer transition duration-200"
                                >
                                    <input
                                    type="radio"
                                    name="starter"
                                    value={starter.name}
                                    checked={ values.isChildMeal === "true" }
                                    onChange={handleChange}
                                    />
                                    {renderRadio(starter)}
                                </label>
                            ))}
                            </AdaptiveContainer>

                            <Title size="2">Mains<span className="text-red-600">*</span></Title>
                            <AdaptiveContainer columns={1}>
                            { errors.main && touched.main && <div className="text-red-500">{errors.main}</div>}
                            { childrenMenuJson.mains.map((main : Dish) => (
                                <label
                                    key={main.name}
                                    className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer transition duration-200"
                                >
                                    <input
                                    type="radio"
                                    name="main"
                                    value={main.name}
                                    checked={ values.isChildMeal === "true" }
                                    onChange={handleChange}
                                    className="h-5 w-5 text-blue-500 border-gray-300 focus:ring-blue-500"
                                    />
                                    {renderRadio(main)}
                                </label>
                            ))}
                            </AdaptiveContainer>

                            <Title size="2">Desserts<span className="text-red-600">*</span></Title>
                            <AdaptiveContainer columns={1}>
                            { errors.dessert && touched.dessert && <div className="text-red-500">{errors.dessert}</div>}
                            { childrenMenuJson.desserts.map((dessert : Dish) => (
                                <label
                                    key={dessert.name}
                                    className="flex items-center space-x-3 p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer transition duration-200"
                                >
                                    <input
                                    type="radio"
                                    name="dessert"
                                    value={dessert.name}
                                    checked={ values.isChildMeal === "true" }
                                    onChange={handleChange}
                                    className="h-5 w-5 text-blue-500 border-gray-300 focus:ring-blue-500"
                                    />
                                    {renderRadio(dessert)}
                                </label>
                            ))}
                            </AdaptiveContainer>
                            </div>
                        )}
                            <AdaptiveContainer columns={1}>
                                <label htmlFor="otherRequests" className="font-bold font-lora">Any other requests?</label>
                                <Text className='pt-0'>If there are any requests to make something gluten free or vegan as detailed in the menu item, then please state those requests here. </Text>
                                <textarea
                                    name="otherRequests"
                                    id="otherRequests"
                                    value={values.otherRequests}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                                    rows={4}
                                />
                            </AdaptiveContainer>
                        </AdaptiveContainer>
                        
                        )
                    }
                    {
                        values.attending === "true" && (
                            <AdaptiveContainer columns={1}>
                                { errors.songRequest && touched.songRequest && <div className="text-red-500">{errors.songRequest}</div>}
                                <label htmlFor="songRequest" className="font-bold font-lora">Song Request</label>
                                <input type="text" name="songRequest" id="songRequest" value={values.songRequest} onChange={handleChange}  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200" />
                            </AdaptiveContainer>
                        )
                    }
                    {!isValid && (
                        <span className="text-red-500 mb-4">
                            Please ensure all required fields are filled out correctly. Scroll up and check the errors.
                        </span>
                    )}
                    <input type="submit" value={ rsvpFormLoading ? "Submitting" : "Submit"} className='bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded my-6 sm:w-1/5 font-lora' />
                </form>)}
            </Formik>
        </MainContainer>
    )
})

export default RSVP;